import type { ReactNode } from 'react'
import type { FleetUnitView } from '@/shared/api/__generated__'
import { cn, formatDateTime } from '@/shared/lib'
import { Card, CardLink } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { StatusBadge } from '@/shared/ui/status-badge'
import { unitStatus } from '../model/statuses'
import { SlotsMeter } from './slots-meter'

interface UnitCardProps {
  unit: FleetUnitView
  providerName?: string
  active?: boolean
  // Удалённый юнит карточки на бэке не имеет (404) — такая запись остаётся
  // в списке историей и ссылкой не притворяется.
  linked?: boolean
  actions?: ReactNode
}

export function UnitCard({
  unit,
  providerName,
  active = false,
  linked = true,
  actions,
}: UnitCardProps) {
  const status = unitStatus(unit.status)
  const busy = unit.slots_busy ?? 0

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-body-sm font-medium">{unit.id}</span>
        {/* Устаревший доклад — не «звонки идут», а «юнит молчит»: занятость
            ниже это снимок, а не «сейчас». */}
        <span
          className={cn(
            'shrink-0 text-caption',
            unit.heartbeat_stale ? (active ? 'text-white' : 'text-warn') : undefined,
            !unit.heartbeat_stale && (active ? 'text-white/70' : 'text-muted'),
          )}
        >
          {unit.heartbeat_stale ? 'доклад устарел · ' : ''}
          {formatDateTime(unit.heartbeat_at)}
        </span>
      </div>
      <p className={cn('mt-0.5 truncate text-body-sm', active ? 'text-white/80' : 'text-muted')}>
        {unit.gpu_model}
        {providerName ? ` · ${providerName}` : ''}
      </p>
      <div
        className={cn(
          'mt-3 flex flex-col gap-2 rounded-control px-3 py-2',
          active ? 'bg-black/25' : 'bg-raise',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Chip tone={unit.class === 'C' ? 'accent' : 'neutral'}>{unit.class}</Chip>
            <StatusBadge {...status} />
          </div>
          <span className={cn('text-caption', active ? 'text-white/70' : 'text-muted')}>
            занято {busy} из {unit.hb_slots_total}
          </span>
        </div>
        {unit.pending_reports > 0 && (
          <span className={cn('text-caption', active ? 'text-white' : 'text-danger')}>
            отчётов в пути {unit.pending_reports}
          </span>
        )}
        <SlotsMeter
          busy={busy}
          total={unit.hb_slots_total}
          plan={unit.capacity_slots}
          onGradient={active}
        />
      </div>
    </>
  )

  if (!linked) {
    return (
      <Card>
        {body}
        {actions && <div className="mt-3 flex items-center gap-2">{actions}</div>}
      </Card>
    )
  }

  return (
    <CardLink to="/units/$unitId" params={{ unitId: unit.id }} active={active}>
      {body}
    </CardLink>
  )
}
